'use client';

import { cn, JSFunction } from '@ahm/common-helpers';
import dayjs from 'dayjs';
import Fuse from 'fuse.js';
import { useParams } from 'next/navigation';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { AIAssistantMessage } from '../(main)/store/[storeSlug]/components/ai-assistant-message';
import { DesktopHeader } from '../(main)/store/[storeSlug]/components/desktop-header';
import { NoSearchResults } from '../(main)/store/[storeSlug]/components/no-search-results';
import { OrderDialog } from '../(main)/store/[storeSlug]/components/order-dialog';
import { SearchInterface } from '../(main)/store/[storeSlug]/components/search-interface';
import { StoreHeader } from '../(main)/store/[storeSlug]/components/store-header';
import { TabNavigation } from '../(main)/store/[storeSlug]/components/tab-navigation';
import { LoginDialog } from '../(main)/store/components/fb-login';
import MaxDistanceDialog from '../(main)/store/components/max-distance-dialog';
import { NewMobileHero } from './mobile-hero';
import type { Store } from '@/api/ahafood';
import { useGetMenuWS, type Item } from '@/api/use-get-menu-ws';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { useWebstoreSession } from '@/app/[locale]/(order)/components/auth/webstore-session-provider';
import { CartFooter } from '@/app/[locale]/(order)/order/[storeId]/cart/cart-footer';
import { MenuItems } from '@/app/[locale]/(order)/order/[storeId]/components/menu-items';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useMessenger } from '@/hooks/use-messenger';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useAiFoodOrderStore, useAuthStore } from '@/store';
import { useCartStore } from '@/store/cart/cart-store-provider';
import type { MenuItem } from '@/types/item';

// Custom styles cho scrollable tabs
const tabScrollStyles = `
  .scrollable-tabs {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scroll-behavior: smooth; /* Smooth scrolling */
  }
  .scrollable-tabs::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
  /* Smooth scroll padding for better UX */
  .tab-button {
    scroll-margin-left: 16px;
    scroll-margin-right: 16px;
  }
`;

// Constants from useStoreDetail
const WEBSTORE_SLUG_KEY = 'ahafood.webstore_slug';

const FUSE_OPTIONS = {
  keys: ['name', 'description'],
  threshold: 0.4,
  includeScore: true,
};

type Props = {
  store: Store;
};

export default function StoreDetailPage({ store }: Props) {
  // Hooks from useStoreDetail
  const isMobile = useIsMobile();
  const cityId = useAiFoodOrderStore.use.city_id();
  const address = useAiFoodOrderStore.use.address();
  const params = useParams();
  const { user } = useAuthStore();
  const isWebinApp = useDetectWebInApp();
  const goToOrderWebstore = useGoToOrderWebstore();

  // Cart store hooks
  const setBranchId = useCartStore((state) => state.setBranchId);
  const currentBranchId = useCartStore((state) => state.branchId);
  const setStoreContext = useCartStore((state) => state.setStoreContext);
  const items = useCartStore((state) => state.items);
  const currentStoreId = useCartStore((state) => state.currentStoreId);

  useWebstoreSession();

  // Use custom hook để fetch menu data
  const { data: menuData, isLoading: loadingMenu } = useGetMenuWS(
    store?._id ?? 0,
    {
      enabled: !!store?._id,
    }
  );

  // State management tương tự StoreDetailsContainer
  const [activeTab, setActiveTab] = useState('all');
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMaxDistanceDialog, setShowMaxDistanceDialog] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  const { openCitySheet, isNavigating } = useStoreCitySheet();

  const itemTypeRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Check if address is missing and show address sheet
  useEffect(() => {
    if (!loadingMenu && store && !address && !isNavigating) {
      openCitySheet();
    }
  }, [address, store, loadingMenu, isNavigating, openCitySheet]);
  // Get store slug from params
  const storeSlug =
    typeof params.storeSlug === 'string' ? params.storeSlug : store?.slug || '';

  // Item types processing
  const itemTypes = menuData?.item_types ?? [];
  const displayItemTypes = itemTypes.filter((type) => type?._id !== undefined);

  // Tính toán data arrays từ menuData
  const itemsArray = useMemo(() => Object.values(items), [items]);
  const hasCartItems = useMemo(() => Object.values(items).length > 0, [items]);

  // Fuse instance for search
  const fuseInstance = useMemo(() => {
    if (!menuData?.item_types) return null;

    const allItems = menuData.item_types.flatMap((itemType) =>
      itemType.items.map((item) => ({ ...item, itemTypeId: itemType._id }))
    );

    return new Fuse(allItems, FUSE_OPTIONS);
  }, [menuData?.item_types]);

  // Filter items theo search query using Fuse.js
  const finalItemTypesToDisplay = useMemo(() => {
    if (!searchQuery?.trim() || !menuData?.item_types || !fuseInstance) {
      return menuData?.item_types ?? [];
    }

    const searchResults = fuseInstance.search(searchQuery);
    const itemTypeMap = new Map();

    menuData.item_types.forEach((itemType) => {
      itemTypeMap.set(itemType._id, { ...itemType, items: [] });
    });

    searchResults.forEach(({ item }) => {
      const itemType = itemTypeMap.get(item.itemTypeId);
      if (itemType) {
        itemType.items.push(item);
      }
    });

    return Array.from(itemTypeMap.values()).filter(
      (itemType) => itemType.items.length > 0
    );
  }, [menuData?.item_types, searchQuery, fuseInstance]);

  const hasNoSearchResults = useMemo(() => {
    return searchQuery?.trim() && finalItemTypesToDisplay.length === 0;
  }, [searchQuery, finalItemTypesToDisplay]);

  const shouldShowMenuItems = menuData && menuData.item_types.length > 0;

  // Messenger hooks
  const { handleChatClick, isAuthenticated } = useMessenger({
    showLoginDialog: setShowLoginDialog,
    onActionCompleted: () => {
      setShowLoginDialog(true);
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginDialog(false);
    }
  }, [isAuthenticated]);

  // Store context effects
  useEffect(() => {
    if (!store?.nearest_branch?.id) return;

    const newBranchId = store.nearest_branch.id;
    const storeId = store._id.toString();

    // Chỉ cần set branch và store context đơn giản
    // Không clear cart khi cùng store (để giữ cart khi reload)
    if (currentBranchId !== newBranchId) {
      console.log('Setting up store context:', {
        branchId: newBranchId,
        storeId,
        storeSlug,
      });

      // Chỉ clear cart nếu thực sự là store khác (khác storeId)
      if (currentStoreId && currentStoreId !== storeId) {
        console.log('Different store detected, clearing cart:', {
          currentStoreId,
          newStoreId: storeId,
        });
        // Logic clear được handle trong setStoreContext
      }

      // Update store context
      setStoreContext({
        branchId: newBranchId,
        storeId,
        storeSlug,
        pageId: store.page_id.toString(),
      });
    }

    // Luôn đảm bảo branchId được set
    setBranchId(newBranchId, storeId);

    // Update localStorage chỉ khi cần
    if (typeof window !== 'undefined') {
      const currentSlug = localStorage.getItem(WEBSTORE_SLUG_KEY);
      if (currentSlug !== storeSlug) {
        localStorage.setItem(WEBSTORE_SLUG_KEY, storeSlug);
        localStorage.setItem(
          'ahafood.webstore_page_id',
          store.page_id.toString()
        );
      }
    }
  }, [
    store?._id,
    store?.nearest_branch?.id,
    store?.page_id,
    store?.slug,
    currentBranchId,
    storeSlug,
    setBranchId,
    setStoreContext,
    currentStoreId,
  ]);

  // Max distance dialog effect
  useEffect(() => {
    if (store?.distance && store?.distance > 20000) {
      setShowMaxDistanceDialog(true);
    }
  }, [store?.distance]);

  // Detect mobile - remove this as we use useIsMobile hook now
  // useEffect(() => {
  //   const checkMobile = () => {
  //     setIsMobile(window.innerWidth < 768);
  //   };
  //   checkMobile();
  //   window.addEventListener('resize', checkMobile);
  //   return () => window.removeEventListener('resize', checkMobile);
  // }, []);

  // Enhanced Handlers from useStoreDetail
  const toggleSearch = useCallback(() => {
    if (showSearch) {
      setShowSearch(false);
      setSearchQuery('');
      return;
    }
    setShowSearch(true);

    setTimeout(() => {
      const searchInterface = document.getElementById('search-interface');
      if (searchInterface) {
        searchInterface.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
      setTimeout(() => {
        document.getElementById('store-search-input')?.focus();
      }, 100);
    }, 200);
  }, [showSearch]);

  const handleSendMessage = useCallback(
    (pageId: number) => {
      trackEvent('click_button', {
        is_login: isAuthenticated ? 'true' : 'false',
        event_timestamp: dayjs().unix(),
        button_name: 'order_now',
        city_id: cityId,
        button_location: 'store_detail',
        location_text: address?.address,
        lat: address?.lat,
        lng: address?.lng,
        fb_id: user?.id,
        user_id: user?.cunstomerId,
        store_name: store?.name,
        store_id: store?._id.toString(),
        store_page_id: store?.page_id.toString(),
      });
      if (isAuthenticated) {
        handleChatClick(pageId);
        setShowOrderDialog(false);
      } else {
        handleChatClick(pageId);
      }
    },
    [isAuthenticated, cityId, address, user, store, handleChatClick]
  );

  const openFacebookOnDesktop = useCallback((pageId: number) => {
    window.open(`https://www.facebook.com/${pageId}`, '_blank');
  }, []);

  const isValidPageId = useCallback((pageId: number) => {
    return pageId && pageId > 0;
  }, []);

  const handleOpenFacebook = useCallback(
    (pageId: number) => {
      if (!isValidPageId(pageId)) {
        toast.error('Không thể mở fanpage của cửa hàng này, vui lòng thử lại.');
        return;
      }

      if (isMobile && isWebinApp) {
        const deeplink = `fb://page/${pageId}`;
        const fallback = `https://www.facebook.com/${pageId}`;

        JSFunction.call({
          name: 'openPage',
          body: {
            url: deeplink,
            fallback,
            type: 'external_app',
          },
        });
      } else if (isMobile) {
        const deeplink = `fb://page/${pageId}`;
        const fallback = `https://www.facebook.com/${pageId}`;

        const tempLink = document.createElement('a');
        tempLink.href = deeplink;
        tempLink.click();

        setTimeout(() => {
          window.open(fallback, '_blank');
        }, 1000);
      } else {
        openFacebookOnDesktop(pageId);
      }
    },
    [isMobile, isValidPageId, isWebinApp, openFacebookOnDesktop]
  );

  const handleItemClick = useCallback(
    (item: Item) => {
      const menuItem: MenuItem = {
        ...item,
        status:
          item.status === 'ACTIVE' || item.status === 'INACTIVE'
            ? item.status
            : undefined,
        discount_config: item.discount_config
          ? {
              ...item.discount_config,
              type:
                item.discount_config.type === 'VALUE' ||
                item.discount_config.type === 'PERCENTAGE'
                  ? item.discount_config.type
                  : 'VALUE',
            }
          : undefined,
      };
      if (store?.distance && store.distance > 20000) {
        toast.error(
          'Địa chỉ của bạn nằm ngoài vùng giao hàng, vui lòng chọn một địa chỉ khác trong phạm vi 20km.'
        );
        setShowMaxDistanceDialog(true);
        return;
      }
      if (store?.nearest_branch?.id) {
        if (isAuthenticated) {
          setSelectedItem(item);
          goToOrderWebstore(
            store.page_id.toString(),
            store.slug,
            store.nearest_branch.id,
            menuItem,
            store._id.toString()
          );
        } else {
          setShowLoginDialog(true);
        }
      }
    },
    [goToOrderWebstore, store, isAuthenticated]
  );

  const handleCloseDialog = useCallback(() => {
    setShowOrderDialog(false);
    setSelectedItem(null);
  }, []);

  const handleItemTypeClick = useCallback(
    (itemTypeId: string) => {
      setActiveTab(itemTypeId?.toString() || '');

      const element = itemTypeRefs.current[itemTypeId];
      if (element) {
        let offset = 0;

        if (isMobile) {
          if (showSearch) {
            offset = 81;
          }
        } else {
          offset = 80;
          if (showSearch) {
            offset += 81;
          }
        }

        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;
        const additionalOffset = 16;

        window.scrollTo({
          top: elementPosition - offset - additionalOffset,
          behavior: 'smooth',
        });
      }
    },
    [isMobile, showSearch]
  );

  const calculateScrollOffset = useCallback(
    (showSearch: boolean, isMobile: boolean) => {
      let offset = 0;

      if (isMobile) {
        if (showSearch) {
          offset = 81;
        }
        const tabsContainer = document.getElementById('tabs-container');
        if (tabsContainer) {
          offset += tabsContainer.offsetHeight;
        }
      } else {
        offset = 80;
        if (showSearch) {
          offset += 81;
        }
        const tabsContainer = document.getElementById('tabs-container');
        if (tabsContainer) {
          offset += tabsContainer.offsetHeight;
        }
      }

      return offset;
    },
    []
  );

  const handleAllTabClick = useCallback(() => {
    setActiveTab('all');
    const firstItemType = displayItemTypes[0];
    if (firstItemType?._id) {
      const element = itemTypeRefs.current[firstItemType._id.toString()];
      if (element) {
        const offset = calculateScrollOffset(showSearch, isMobile);
        const additionalOffset = 16;
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
          top: elementPosition - offset - additionalOffset,
          behavior: 'smooth',
        });
      }
    }
  }, [displayItemTypes, showSearch, isMobile, calculateScrollOffset]);

  const handleAskAI = useCallback(() => {
    trackEvent('chatbot_event', {
      button_name: 'ask_ai',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
    });
    if (
      typeof window !== 'undefined' &&
      typeof window.setOpenChange === 'function'
    ) {
      window.setOpenChange(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show loading state
  if (loadingMenu) {
    return (
      <>
        <DesktopHeader />
        <div className="w-full mx-auto min-h-screen bg-white">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full size-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-neutral-60">Đang tải menu...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <DesktopHeader />
      <div className="w-full mx-auto min-h-screen bg-white">
        <style dangerouslySetInnerHTML={{ __html: tabScrollStyles }} />

        {/* Mobile Container */}
        <div className="md:hidden">
          <NewMobileHero store={store} onToggleSearch={toggleSearch} />
          <StoreHeader
            store={store}
            onOpenFacebook={() => handleOpenFacebook(store?.page_id)}
            onSendMessage={() => handleSendMessage(store?.page_id)}
            isMobileView={true}
          />
          <AIAssistantMessage onAskAI={handleAskAI} />
        </div>

        {/* Desktop Container */}
        <div className="hidden md:block md:max-w-6xl md:mx-auto md:px-6 md:py-8">
          <div className="md:grid md:grid-cols-12 md:gap-8">
            {/* Main content area */}
            <div
              className={cn(
                itemsArray.length > 0 ? 'md:col-span-8' : 'md:col-span-12'
              )}
            >
              <StoreHeader
                store={store}
                onOpenFacebook={() => handleOpenFacebook(store?.page_id)}
                onSendMessage={() => handleSendMessage(store?.page_id)}
                isMobileView={false}
              />
              <AIAssistantMessage onAskAI={handleAskAI} />

              {/* Menu Section */}
              <div className="w-full">
                <SearchInterface
                  showSearch={showSearch}
                  searchQuery={searchQuery}
                  isMobile={isMobile}
                  onSearchChange={setSearchQuery}
                  onCancel={() => {
                    setSearchQuery('');
                    setShowSearch(false);
                  }}
                />

                {/* Show search results or regular content */}
                {hasNoSearchResults ? (
                  <NoSearchResults
                    searchQuery={searchQuery}
                    onClearSearch={() => setSearchQuery('')}
                  />
                ) : (
                  <>
                    <TabNavigation
                      activeTab={activeTab}
                      displayItemTypes={displayItemTypes}
                      showSearch={showSearch}
                      isMobile={isMobile}
                      onAllTabClick={handleAllTabClick}
                      onItemTypeClick={handleItemTypeClick}
                    />

                    {shouldShowMenuItems && (
                      <MenuItems
                        itemTypes={finalItemTypesToDisplay}
                        itemTypeRefs={itemTypeRefs}
                        onItemClick={handleItemClick}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Desktop cart sidebar */}
            {shouldShowMenuItems && (
              <div className="hidden md:block md:col-span-4">
                <div className="sticky top-28">
                  <CartFooter />
                </div>
              </div>
            )}
            <MaxDistanceDialog
              open={showMaxDistanceDialog && !isMobile}
              onClose={() => setShowMaxDistanceDialog(false)}
            />
          </div>
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden w-full">
          <SearchInterface
            showSearch={showSearch}
            searchQuery={searchQuery}
            isMobile={isMobile}
            onSearchChange={setSearchQuery}
            onCancel={() => {
              setSearchQuery('');
              setShowSearch(false);
            }}
          />

          {/* Show search results or regular content */}
          {hasNoSearchResults ? (
            <NoSearchResults
              searchQuery={searchQuery}
              onClearSearch={() => setSearchQuery('')}
            />
          ) : (
            <>
              <TabNavigation
                activeTab={activeTab}
                displayItemTypes={displayItemTypes}
                showSearch={showSearch}
                isMobile={isMobile}
                onAllTabClick={handleAllTabClick}
                onItemTypeClick={handleItemTypeClick}
              />

              {shouldShowMenuItems && (
                <MenuItems
                  itemTypes={finalItemTypesToDisplay}
                  itemTypeRefs={itemTypeRefs}
                  onItemClick={handleItemClick}
                />
              )}
            </>
          )}
        </div>

        {/* Mobile cart footer */}
        <div className="md:hidden">
          {shouldShowMenuItems && hasCartItems && <CartFooter />}
          <MaxDistanceDialog
            open={showMaxDistanceDialog && isMobile}
            onClose={() => setShowMaxDistanceDialog(false)}
          />
        </div>

        {/* Order Dialog/Drawer */}
        <OrderDialog
          showOrderDialog={showOrderDialog}
          selectedItem={selectedItem}
          store={store}
          isMobile={isMobile}
          onClose={handleCloseDialog}
          onSendMessage={() => handleSendMessage(store?.page_id)}
        />
      </div>

      {/* Login Dialog */}
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="store_detail"
      />
    </>
  );
}
